import React from "react";
import { Page, Text, View, Document, StyleSheet, Font } from "@react-pdf/renderer";
import Roboto from "../../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../../../assets/fonts/Roboto/Roboto-Bold.ttf";

Font.register({
  family: "Roboto",
  fonts: [{ src: Roboto }, { src: RobotoBold, fontWeight: 600 }],
  format: "truetype",
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    color: "#333",
    fontFamily: "Roboto",
  },
  reportTypeTitle: {
    fontSize: 11,
    fontWeight: 700,
    textAlign: "center",
    lineHeight: 1.3,
    marginBottom: 8,
  },
  metaTable: {
    marginTop: 4,
    marginBottom: 4,
    border: "1px solid #cdeed3",
    borderRadius: 4,
  },
  metaRow: {
    flexDirection: "row",
    borderBottom: "1px solid #cdeed3",
  },
  metaRowLast: {
    borderBottomWidth: 0,
  },
  metaLabelCell: {
    width: "35%",
    paddingVertical: 3,
    paddingHorizontal: 6,
    backgroundColor: "#eaf7ec",
    justifyContent: "center",
  },
  metaValueCell: {
    width: "65%",
    paddingVertical: 3,
    paddingHorizontal: 6,
    justifyContent: "center",
  },
  metaLabelCellHalf: {
    width: "17.5%",
    paddingVertical: 3,
    paddingHorizontal: 6,
    backgroundColor: "#eaf7ec",
    justifyContent: "center",
  },
  metaValueCellHalf: {
    width: "32.5%",
    paddingVertical: 3,
    paddingHorizontal: 6,
    justifyContent: "center",
  },
  metaValueCellHalfDivider: {
    borderRight: "1px solid #cdeed3",
  },
  metaLabelText: {
    fontSize: 9,
    fontWeight: 600,
    lineHeight: 1.15,
  },
  metaValueText: {
    fontSize: 9,
    color: "#1b3a6b",
    lineHeight: 1.15,
  },
  metaValueSecondaryText: {
    fontSize: 8,
    color: "#555",
    marginTop: 1,
    lineHeight: 1.15,
  },
  table: {
    marginTop: 8,
    border: "1px solid #ddd",
  },
  tableHeadRow: {
    flexDirection: "row",
    backgroundColor: "#f2f2f7",
    borderBottom: "1px solid #ddd",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #eee",
  },
  cellDate: { width: "26%", paddingVertical: 2, paddingHorizontal: 5 },
  cellIn: { width: "18%", paddingVertical: 2, paddingHorizontal: 5 },
  cellOut: { width: "18%", paddingVertical: 2, paddingHorizontal: 5 },
  cellHours: { width: "18%", paddingVertical: 2, paddingHorizontal: 5 },
  cellStatus: { width: "20%", paddingVertical: 2, paddingHorizontal: 5 },
  headText: {
    fontSize: 8,
    fontWeight: 600,
    lineHeight: 1.15,
  },
  cellText: {
    fontSize: 8,
    lineHeight: 1.15,
  },
  statusBadge: {
    fontSize: 7,
    fontWeight: 600,
    alignSelf: "flex-start",
  },
  panelsRow: {
    flexDirection: "row",
    marginTop: 8,
  },
  panel: {
    flex: 1,
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    padding: 10,
  },
  panelSpacer: {
    width: 10,
  },
  panelHeader: {
    fontSize: 10,
    fontWeight: 700,
    paddingBottom: 6,
    marginBottom: 8,
    borderBottom: "1px solid #eee",
  },
  legendItemRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  legendBadge: {
    fontSize: 7,
    fontWeight: 700,
    borderRadius: 3,
    paddingVertical: 3,
    paddingHorizontal: 6,
    marginRight: 8,
    minWidth: 26,
    textAlign: "center",
  },
  legendLabel: {
    fontSize: 8,
    color: "#333",
  },
  summaryItemRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  summaryDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  summaryLabel: {
    fontSize: 8,
    color: "#333",
    flex: 1,
  },
  summaryValue: {
    fontSize: 10,
    fontWeight: 700,
  },
});

const STATUS_LEGEND = [
  { code: "W/O", meaning: "Weekly Off", bg: "#dbeafe", color: "#1d4ed8" },
  { code: "NH", meaning: "National Holiday", bg: "#dcfce7", color: "#15803d" },
  { code: "FH", meaning: "Festival Holiday", bg: "#ede9fe", color: "#7c3aed" },
];

const TimeCardPage = ({
  employee,
  dateRange,
  companyName,
  companyAddress,
  reportGeneratedOn,
}) => {
  if (!employee) return null;
  const detailRows = employee.detailRows || [];

  // Mirrors the same metadata block shown on the aggregate report export —
  // just scoped to this one employee instead of "All". The identity/context
  // rows lead the page; the summary totals are repeated as a recap after the
  // attendance table instead of sitting above it.
  const metaHeaderRows = [
    {
      label: "Company Name",
      value: companyName || "N/A",
      secondary: companyAddress || "",
      fullWidth: true,
    },
    { label: "Department", value: employee.department_name || "-" },
    {
      label: "Employee",
      value: `${employee.employee_name || "-"} (${employee.employee_id || "-"})`,
    },
    { label: "Designation", value: employee.designation || "-" },
    { label: "Date of Join", value: employee.date_of_join || "-" },
    { label: "Date Range", value: dateRange || "-" },
    { label: "Report Generated On", value: reportGeneratedOn || "-" },
    {
      label: "Report Type",
      value:
        "Monthly Consolidation Report form 25B section rule no :103, License No. ERO02118",
    },
    { label: "Lunch Hours", value: employee.lunch_hours || "-" },
  ];

  // Shown one item per row instead of split into side-by-side columns.
  const summaryItems = [
    { label: "Total Present", value: employee.present_count ?? 0, color: "#16a34a" },
    { label: "Total Absent", value: employee.absent_count ?? 0, color: "#dc2626" },
    { label: "Total Holiday", value: employee.holiday_count ?? 0, color: "#7c3aed" },
    { label: "Total Weekly Off", value: employee.week_off_count ?? 0, color: "#2563eb" },
  ];

  // Full-width rows (Company Name) stand alone; every other row is paired
  // two-per-line in the order given, matching the payslip-style layout.
  const groupMetaRows = (rows) => {
    const groups = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      if (row.fullWidth) {
        groups.push([row]);
        continue;
      }
      const next = rows[i + 1];
      if (next && !next.fullWidth) {
        groups.push([row, next]);
        i += 1;
      } else {
        groups.push([row]);
      }
    }
    return groups;
  };

  const renderMetaTable = (rows) => {
    const groupedRows = groupMetaRows(rows);

    return (
      <View style={styles.metaTable} wrap={false}>
        {groupedRows.map((group, index) => (
          <View
            key={group.map((item) => item.label).join("-")}
            wrap={false}
            style={
              index === groupedRows.length - 1
                ? [styles.metaRow, styles.metaRowLast]
                : styles.metaRow
            }
          >
            {group.map((item, itemIndex) => (
              <React.Fragment key={item.label}>
                <View
                  style={
                    group.length === 2
                      ? styles.metaLabelCellHalf
                      : styles.metaLabelCell
                  }
                >
                  <Text style={styles.metaLabelText}>{item.label}</Text>
                </View>
                <View
                  style={
                    group.length === 2
                      ? itemIndex === 0
                        ? [styles.metaValueCellHalf, styles.metaValueCellHalfDivider]
                        : styles.metaValueCellHalf
                      : styles.metaValueCell
                  }
                >
                  <Text style={styles.metaValueText}>{item.value}</Text>
                  {item.secondary ? (
                    <Text style={styles.metaValueSecondaryText}>{item.secondary}</Text>
                  ) : null}
                </View>
              </React.Fragment>
            ))}
          </View>
        ))}
      </View>
    );
  };

  const renderSummaryList = (items) => (
    <View>
      {items.map((item) => (
        <View style={styles.summaryItemRow} key={item.label}>
          <View style={[styles.summaryDot, { backgroundColor: item.color }]} />
          <Text style={styles.summaryLabel}>{item.label}</Text>
          <Text style={[styles.summaryValue, { color: item.color }]}>
            {item.value}
          </Text>
        </View>
      ))}
    </View>
  );

  return (
    <Page size="A4" style={styles.page}>
      <Text style={styles.reportTypeTitle}>Time card</Text>

      {renderMetaTable(metaHeaderRows)}

      <View style={styles.table}>
        <View style={styles.tableHeadRow} fixed>
          <View style={styles.cellDate}>
            <Text style={styles.headText}>Date</Text>
          </View>
          <View style={styles.cellIn}>
            <Text style={styles.headText}>In Time</Text>
          </View>
          <View style={styles.cellOut}>
            <Text style={styles.headText}>Out Time</Text>
          </View>
          <View style={styles.cellHours}>
            <Text style={styles.headText}>Total Hours</Text>
          </View>
          <View style={styles.cellStatus}>
            <Text style={styles.headText}>Status</Text>
          </View>
        </View>

        {detailRows.length === 0 ? (
          <View style={styles.tableRow}>
            <View style={{ padding: 6 }}>
              <Text style={styles.cellText}>
                No attendance records in this date range.
              </Text>
            </View>
          </View>
        ) : (
          detailRows.map((row, index) => (
            <View
              style={styles.tableRow}
              key={`${row?.raw_date || index}-${row?.id || index}`}
              wrap={false}
            >
              <View style={styles.cellDate}>
                <Text style={styles.cellText}>{row?.attendance_date || "-"}</Text>
              </View>
              <View style={styles.cellIn}>
                <Text style={styles.cellText}>{row?.in_time || "-"}</Text>
              </View>
              <View style={styles.cellOut}>
                <Text style={styles.cellText}>{row?.out_time || "-"}</Text>
              </View>
              <View style={styles.cellHours}>
                <Text style={styles.cellText}>{row?.total_hours || "-"}</Text>
              </View>
              <View style={styles.cellStatus}>
                <Text
                  style={[
                    styles.statusBadge,
                    { color: row?.status_color || "#6D7483" },
                  ]}
                >
                  {row?.status_display || "-"}
                </Text>
              </View>
            </View>
          ))
        )}
      </View>

      <View style={styles.panelsRow} wrap={false}>
        <View style={styles.panel}>
          <Text style={styles.panelHeader}>Legend</Text>
          {STATUS_LEGEND.map((item) => (
            <View style={styles.legendItemRow} key={item.code}>
              <Text
                style={[
                  styles.legendBadge,
                  { backgroundColor: item.bg, color: item.color },
                ]}
              >
                {item.code}
              </Text>
              <Text style={styles.legendLabel}>{item.meaning}</Text>
            </View>
          ))}
        </View>

        <View style={styles.panelSpacer} />

        <View style={styles.panel}>
          <Text style={styles.panelHeader}>Summary</Text>
          {renderSummaryList(summaryItems)}
        </View>
      </View>

    </Page>
  );
};

const BulkTimeCardData = ({
  employeesPdfData = [],
  dateRange,
  companyName,
  companyAddress,
  reportGeneratedOn,
}) => (
  <Document>
    {employeesPdfData.map((employee, index) => (
      <TimeCardPage
        key={employee?.employee_id || index}
        employee={employee}
        dateRange={dateRange}
        companyName={companyName}
        companyAddress={companyAddress}
        reportGeneratedOn={reportGeneratedOn}
      />
    ))}
  </Document>
);

export default BulkTimeCardData;
